import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaUmbrellaBeach,
  FaUserFriends,
  FaPlaneDeparture,
  FaClipboardList,
  FaShieldAlt,
  FaPassport,
  FaClock,
  FaQuestionCircle,
  FaExternalLinkAlt,
  FaArrowRight,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";

/*
  Tourist / Visitor Visa (Subclass 600) — Modern animated UI
  - Gradient hero with floating blobs
  - Tilted feature cards and steps
  - Readiness checklist with live status
  - Sticky quick‑nav (desktop)
  - Accessible animated FAQ
  - Clean CTA (no "Free" wording)
*/

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const FEATURES = [
  { icon: <FaUmbrellaBeach />, title: "Tourist", body: "Holiday or visit friends and family across Australia." },
  { icon: <FaUserFriends />, title: "Sponsored Family", body: "Be sponsored by an eligible Australian family member." },
  { icon: <FaPlaneDeparture />, title: "Business Visitor", body: "Short business visits – meetings, conferences, site visits." },
  { icon: <FaClock />, title: "Stay options", body: "Typically 3–12 months; single or multiple entry." },
];

const FAQ = [
  { q: "Can I work on a 600 visa?", a: "No. Work is not permitted. Short study (e.g., up to 3 months) may be allowed depending on stream and purpose." },
  { q: "Do I need travel insurance?", a: "It isn't mandatory like OSHC, but strongly recommended. Medical costs in Australia can be high." },
  { q: "What affects processing times?", a: "Your stream, nationality, travel history, and the completeness/clarity of your evidence package." },
  { q: "Do I need biometrics or health checks?", a: "Some applicants are requested to provide biometrics and/or health checks based on risk profiles and travel history." },
];

export default function TouristVisa600() {
  useEffect(() => { document.title = "Tourist / Visitor Visa (Subclass 600) | Glide Education"; }, []);

  // Readiness checker state
  const [hasPurpose, setHasPurpose] = useState(false);
  const [hasFunds, setHasFunds] = useState(false);
  const [hasTies, setHasTies] = useState(false);
  const [hasItinerary, setHasItinerary] = useState(false);
  const ready = useMemo(() => hasPurpose && hasFunds && hasTies && hasItinerary, [hasPurpose, hasFunds, hasTies, hasItinerary]);

  return (
    <motion.main initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="min-h-screen bg-white">
      {/* HERO */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-12 pb-10 md:pb-14">
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" className="text-3xl md:text-5xl font-extrabold tracking-tight text-blue-900">
            Tourist / Visitor Visa — Subclass 600
          </motion.h1>
          <motion.p variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-3 max-w-3xl text-gray-700">
            Visit Australia for a holiday, to see family or friends, or for short business visitor activities. Choose the stream that best fits your purpose and timeline.
          </motion.p>

          {/* Feature cards */}
          <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <Tilt key={f.title} tiltMaxAngleX={8} tiltMaxAngleY={8} scale={1.02} transitionSpeed={400} glareEnable glareMaxOpacity={0.12} glareColor="#60a5fa" className="rounded-2xl border bg-white/80 p-4 shadow-sm backdrop-blur">
                <div className="flex items-center gap-3 text-blue-900">
                  <span className="text-xl">{f.icon}</span>
                  <div className="font-semibold">{f.title}</div>
                </div>
                <p className="mt-1 text-sm text-gray-700">{f.body}</p>
              </Tilt>
            ))}
          </motion.div>

          {/* Primary actions */}
          <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/visitor-600"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white shadow hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Official Home Affairs page <FaExternalLinkAlt className="opacity-90" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-blue-600 bg-white px-5 py-2.5 font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Speak to a Consultant <FaArrowRight />
            </Link>
          </motion.div>

          <Note className="mt-6">
            Processing time and evidence requirements vary by stream, nationality and travel history. Provide clear reasons for your visit and show ties to your home country.
          </Note>
        </div>
      </div>

      {/* BODY GRID */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 pb-16 md:grid-cols-[1fr_320px]">
        {/* LEFT */}
        <div>
          <Section id="eligibility" title="Eligibility (general)">
            <ul className="list-disc pl-5 space-y-2">
              <li>Genuine temporary visitor with adequate funds</li>
              <li>Health & character requirements</li>
              <li>Meet stream‑specific criteria (e.g., sponsorship, business purpose)</li>
            </ul>
          </Section>

          <Section id="stay" title="Stay period & conditions">
            <ul className="list-disc pl-5 space-y-2">
              <li>Typically 3–12 months; single or multiple entry</li>
              <li>No work. Limited study permitted (e.g., up to 3 months)</li>
              <li>No OSHC requirement, but travel insurance is strongly recommended</li>
            </ul>
          </Section>

          {/* Readiness checker */}
          <Section id="readiness" title="Quick readiness check">
            <div className="grid gap-4 md:grid-cols-2">
              <Checklist id="chk-purpose" checked={hasPurpose} onChange={setHasPurpose} icon={<FaClipboardList />} label="I can clearly explain my visit purpose and stream" />
              <Checklist id="chk-funds" checked={hasFunds} onChange={setHasFunds} icon={<FaPassport />} label="I can show sufficient funds and travel evidence" />
              <Checklist id="chk-ties" checked={hasTies} onChange={setHasTies} icon={<FaUserFriends />} label="I can demonstrate ties to my home country" />
              <Checklist id="chk-itinerary" checked={hasItinerary} onChange={setHasItinerary} icon={<FaPlaneDeparture />} label="I have an itinerary (and invitation/sponsorship if relevant)" />
            </div>
            <div className={`mt-4 rounded-lg px-4 py-3 text-sm ${ready ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"}`}>
              {ready ? (
                <span className="inline-flex items-center gap-2 font-medium"><FaCheckCircle /> Looks good! You're broadly ready to prepare your Visitor (600) application.</span>
              ) : (
                <span className="inline-flex items-center gap-2"><FaInfoCircle className="text-blue-600" /> Not sure yet? We’ll review your purpose, funds, ties and itinerary to confirm the strongest stream.</span>
              )}
            </div>
          </Section>

          {/* Steps */}
          <Section id="steps" title="How we assist (step‑by‑step)">
            <div className="grid gap-4 md:grid-cols-3">
              <Step icon={<FaClipboardList />} title="Choose stream" body="We match your purpose to Tourist, Sponsored Family, Business Visitor or ADS." />
              <Step icon={<FaShieldAlt />} title="Evidence pack" body="We assemble clear documentation to reduce back‑and‑forth and delays." />
              <Step icon={<FaClock />} title="Lodge & follow up" body="We lodge via ImmiAccount and help with any additional requests." />
            </div>
          </Section>

          {/* FAQs */}
          <Section id="faq" title="FAQs">
            <div className="space-y-3">
              {FAQ.map((f) => (
                <Accordion key={f.q} title={f.q} body={f.a} />
              ))}
            </div>
          </Section>

          {/* CTA ribbon */}
          <section className="mt-12">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]">
              <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
                <div className="grid items-center gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900">Planning a trip to Australia?</h3>
                    <p className="mt-1 text-gray-600">Speak with a consultant to choose the right stream and build a strong evidence pack.</p>
                  </div>
                  <div className="flex md:justify-end">
                    <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500">
                      Talk to a Consultant <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>
        </div>

        {/* RIGHT: sticky quick nav */}
        <aside className="top-24 hidden md:block">
          <div className="sticky top-24 rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
            <div className="text-sm font-semibold text-blue-900">Quick links</div>
            <nav className="mt-3">
              <ul className="space-y-2 text-sm">
                <li><a className="text-blue-700 hover:underline" href="#eligibility">Eligibility</a></li>
                <li><a className="text-blue-700 hover:underline" href="#stay">Stay & conditions</a></li>
                <li><a className="text-blue-700 hover:underline" href="#readiness">Readiness check</a></li>
                <li><a className="text-blue-700 hover:underline" href="#steps">How we assist</a></li>
                <li><a className="text-blue-700 hover:underline" href="#faq">FAQs</a></li>
              </ul>
            </nav>
            <hr className="my-4 border-blue-100" />
            <a href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/visitor-600" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800 hover:bg-blue-100">
              Official guidance <FaExternalLinkAlt className="opacity-80" />
            </a>
          </div>
        </aside>
      </div>
    </motion.main>
  );
}

/* ---------- Bits ---------- */
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <h2 className="mb-3 text-2xl font-bold text-blue-700">{title}</h2>
      <div className="leading-relaxed text-gray-700">{children}</div>
    </section>
  );
}

function Note({ children, className = "" }) {
  return (
    <div className={`my-4 rounded-md border-l-4 border-blue-600 bg-blue-50 p-4 text-sm ${className}`}>{children}</div>
  );
}

function Checklist({ id, checked, onChange, icon, label }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-400" />
      <span className="flex items-start gap-2 text-sm text-gray-800">
        <span className="mt-0.5 text-blue-600">{icon}</span>
        <span>{label}</span>
      </span>
    </label>
  );
}

function Step({ icon, title, body }) {
  return (
    <Tilt tiltMaxAngleX={8} tiltMaxAngleY={8} scale={1.02} transitionSpeed={400} glareEnable glareMaxOpacity={0.12} glareColor="#60a5fa" className="rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
      <div className="flex items-center gap-3 text-blue-900">
        <span className="text-lg">{icon}</span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-1 text-sm text-gray-700">{body}</p>
    </Tilt>
  );
}

function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  const id = useMemo(() => `acc-${title.toLowerCase().replace(/\s+/g, "-")}`,[title]);

  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg px-1"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={id}
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700" aria-hidden="true">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="mt-2 text-sm leading-relaxed text-gray-700">{body}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}